import React from 'react'
// import { merge } from 'lodash'

import { useTaskAPI } from '../TaskAPIProvider'
import { useTreeState, TYPES } from '../TreeStateProvider'
import useTaskColumnSettings from '../../hooks/useTaskColumnSettings'

const TaskTableStateContext = React.createContext(null)

export const getExpanded = (state, cat) => {
  return state[cat] ?? false
}

export const calcID = (category, isNone) => {
  if (isNone) {
    return 'No Category'
  }

  return category?.id ?? 'null'
}

const TaskTableStateProvider = ({
  category,
  isNone,
  tasks,
  fullPagination,
  children
}) => {
  const { tasks: allTasks, formatTask } = useTaskAPI()
  const {
    state: treeState,
    dispatch: treeDispatch,
    Actions: TreeActions
  } = useTreeState(TYPES.TASK)

  const [expanded, setExpanded] = React.useState(
    getExpanded(treeState, isNone ? 'No Category' : category?.id)
  )

  const name = React.useMemo(() => {
    if (category) {
      return category.name
    }

    if (isNone) {
      return 'No Category'
    }

    return ''
  }, [category, isNone])

  const formattedTasks = React.useMemo(() => {
    if (!tasks) {
      return []
    }

    return tasks.flatMap(task =>
      task.data.map(t => {
        const formatted = formatTask(t)
        return { ...formatted, ...(allTasks[formatted.id] ?? {}) }
      })
    )
  }, [tasks, formatTask, allTasks])

  const hookKey = React.useMemo(
    () => calcID(category, isNone),
    [category, isNone]
  )
  const columnSettings = useTaskColumnSettings(hookKey)

  const toggleExpanded = React.useCallback(() => {
    setExpanded(old => !old)
  }, [])

  React.useEffect(() => {
    treeDispatch({ type: TreeActions.UPDATE, id: hookKey, open: expanded })
  }, [treeDispatch, hookKey, expanded, TreeActions.UPDATE])

  return (
    <TaskTableStateContext.Provider
      value={{
        // Base table values
        tasks: formattedTasks,
        expanded,
        toggleExpanded,
        name,
        id: isNone ? 'No Category' : category?.id ?? null,
        canAdd: true,
        key: hookKey,

        // Column Setting props
        ...columnSettings,

        // Needed for task arrows
        fullPagination
      }}
    >
      {children}
    </TaskTableStateContext.Provider>
  )
}

export default TaskTableStateProvider

export const useTaskTableState = () => React.useContext(TaskTableStateContext)
